import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { api } from '@koridor/api-client';
import { DEFAULT_SETTINGS, type Language, type Theme, type UserSettings } from '@koridor/shared';
import { isGuestUser, useAuth } from './AuthContext';

export const LANG_LABELS: Record<Language, string> = { ru: 'Русский', en: 'English' };
const LANG_CODES: Record<string, Language> = { Русский: 'ru', English: 'en' };

export function resolveIsDark(theme: Theme, mqDark: boolean) {
  return theme === 'dark' || (theme === 'auto' && mqDark);
}

function syncNativeStatusBar(isDark: boolean) {
  if (!Capacitor.isNativePlatform()) return;
  void (async () => {
    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    } catch {
      /* status bar plugin unavailable */
    }
  })();
}

export function applyThemeToDom(theme: Theme) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const isDark = resolveIsDark(theme, mq.matches);
  document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
  localStorage.setItem('koridor_theme', theme);
  syncNativeStatusBar(isDark);
}

function readCachedSettings(): UserSettings {
  const theme = (localStorage.getItem('koridor_theme') as Theme | null) ?? DEFAULT_SETTINGS.theme;
  const langLabel = localStorage.getItem('koridor_lang') ?? LANG_LABELS.ru;
  const language = LANG_CODES[langLabel] ?? DEFAULT_SETTINGS.language;
  let extras: Partial<UserSettings> = {};
  try {
    const raw = localStorage.getItem('koridor_settings');
    if (raw) extras = JSON.parse(raw) as Partial<UserSettings>;
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_SETTINGS, ...extras, theme, language };
}

interface SettingsContextValue {
  settings: UserSettings;
  langLabel: string;
  updateSettings: (patch: Partial<UserSettings>) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(readCachedSettings);

  useEffect(() => {
    if (!user?.settings) return;
    // Guest carries DEFAULT_SETTINGS — keep local/cache theme & toggles.
    if (isGuestUser(user)) return;
    const merged = { ...DEFAULT_SETTINGS, ...user.settings };
    setSettings(merged);
    applyThemeToDom(merged.theme);
    localStorage.setItem('koridor_lang', LANG_LABELS[merged.language]);
    try {
      localStorage.setItem('koridor_settings', JSON.stringify(merged));
    } catch {
      /* ignore */
    }
  }, [user?.id, user?.settings]);

  useEffect(() => {
    applyThemeToDom(settings.theme);
    if (settings.theme !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyThemeToDom('auto');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [settings.theme]);

  const updateSettings = useCallback(async (patch: Partial<UserSettings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    if (patch.theme) applyThemeToDom(patch.theme);
    if (patch.language) localStorage.setItem('koridor_lang', LANG_LABELS[patch.language]);
    try {
      localStorage.setItem('koridor_settings', JSON.stringify(next));
    } catch {
      /* ignore */
    }
    if (api.isAuthenticated) {
      try {
        await api.updateSettings(patch);
        await refreshUser();
      } catch {
        /* offline — local settings still apply */
      }
    }
  }, [settings, refreshUser]);

  return (
    <SettingsContext.Provider value={{
      settings,
      langLabel: LANG_LABELS[settings.language],
      updateSettings,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings outside provider');
  return ctx;
}

export function themeLabelFor(settings: UserSettings, themes: string[]) {
  const idx = settings.theme === 'dark' ? 1 : settings.theme === 'auto' ? 2 : 0;
  return themes[idx] ?? themes[0];
}

export function themeFromLabel(label: string, themes: string[]): Theme {
  const idx = themes.indexOf(label);
  if (idx === 1) return 'dark';
  if (idx === 2) return 'auto';
  return 'light';
}

export function languageFromLabel(label: string): Language {
  return LANG_CODES[label] ?? 'ru';
}
